import {BrowserModule} from '@angular/platform-browser';
import {ElementRef, ErrorHandler, LOCALE_ID, NgModule} from '@angular/core';
import {AppComponent} from './app.component';
import {KENDO_ANGULAR_GRID_CONFIG, MeasurementTypeService} from '@framewerx/kendo-angular-grid';
import {environment} from '../environments/environment';
import {LoggerModule, NGXLogger, NgxLoggerLevel} from 'ngx-logger';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {ICON_SETTINGS, IconsModule} from '@progress/kendo-angular-icons';
import {AppRoutingModule} from './app-routing.module';
import {NgbModule} from '@ng-bootstrap/ng-bootstrap';
import {ThemeModule} from './@theme/theme.module';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptorConfiguration,
  MsalModule,
  MsalRedirectComponent,
  MsalService
} from '@azure/msal-angular';
import {CoreModule} from './@core/core.module';
import {FilterService, GridModule} from '@progress/kendo-angular-grid';
import {DialogsModule} from '@progress/kendo-angular-dialog';
import {DateInputsModule} from '@progress/kendo-angular-dateinputs';
import {InputsModule} from '@progress/kendo-angular-inputs';
import {NbLayoutModule, NbThemeModule} from '@nebular/theme';
import {DropDownsModule} from '@progress/kendo-angular-dropdowns';
import {TooltipModule} from '@progress/kendo-angular-tooltip';
import {kendoGridConfigFactory, SharedModule} from './pages/shared/shared.module';
import {LayoutModule} from '@progress/kendo-angular-layout';
import {PendingChangesGuard} from './pending-changes-guard';
import {MsalCustomInterceptor} from './pages/auth/msal-custom-interceptor';
import {APP_BASE_HREF} from '@angular/common';
import {CldrIntlService, IntlService} from '@progress/kendo-angular-intl';
import {GlobalHttpErrorInterceptorService} from './@core/utils/global-http-error-interceptor.service';
import {ErrorService} from './pages/shared/errors/error.service';
import {GlobalErrorHandlerService} from './@core/utils/global-error-handler.service';
import {ApiModule, BASE_PATH, Configuration, ConfigurationParameters} from '../advanced-bom-api';
// import {
//   ApiModule as StemsApiModule,
//   BASE_PATH as STEMS_BASE_PATH,
//   Configuration as StemsConfiguration,
//   ConfigurationParameters as StemsConfigurationParameters
// } from '../stems-api';
import {BlobErrorHttpInterceptorService} from './@core/utils/blob-error-http-interceptor.service';
import {
  BrowserCacheLocation,
  InteractionType,
  IPublicClientApplication,
  LogLevel,
  PublicClientApplication
} from '@azure/msal-browser';
import {L10N_PREFIX, LocalizationService, MessageService} from '@progress/kendo-angular-l10n';
import {FramewerxKendoAngularGridCompatModule} from './framewerx-kendo-angular-grid-compat.module';
import { POPUP_CONTAINER } from '@progress/kendo-angular-popup';

/**
 * Handles apiconfigfactory for this module.
 */
export function apiConfigFactory(): Configuration {
  const params: ConfigurationParameters = {
    // set configuration parameters here.
    apiKeys: {Authorization: ''},
    basePath: environment.apiBaseUrl,
  };
  return new Configuration(params);
}

// export function stemsApiConfigFactory(): StemsConfiguration {
//   const params: StemsConfigurationParameters = {
//     // set configuration parameters here.
//     basePath: environment.stemsApiBaseUrl,
//   };
//   return new StemsConfiguration(params);
// }

/**
 * Handles loggercallback for this module.
 * @param NGXLogger Specifies the ngxlogger value.
 * @param LogLevel Specifies the loglevel value.
 * @param string Specifies the string value.
 * @param boolean Specifies the boolean value.
 */
export function loggerCallback(logger: NGXLogger, logLevel: LogLevel, message: string, containsPii: boolean) {
  switch (logLevel) {
    case LogLevel.Error:
      logger.error(message);
      break;
    case LogLevel.Info:
      logger.info(message);
      break;
    case LogLevel.Verbose:
      logger.trace(message);
      break;
    case LogLevel.Warning:
      logger.warn(message);
      break;
    default:
      logger.log(message);
      break;
  }
}

const isIE = window.navigator.userAgent.indexOf('MSIE ') > -1 || window.navigator.userAgent.indexOf('Trident/') > -1;

/**
 * Handles msalinstancefactory for this module.
 */
export function MSALInstanceFactory(logger: NGXLogger): IPublicClientApplication {
  const logLevel = environment.msalLoggingLevel || LogLevel.Info;
  return new PublicClientApplication({
    auth: {
      clientId: environment.azureClientId,
      authority: environment.authority,
      redirectUri: `${window.location.origin}/auth`,
      postLogoutRedirectUri: `${window.location.origin}/dashboard`,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage, // localStorage works across tabs but is less secure,
    },
    system: {
      loggerOptions: {
        loggerCallback: (level: LogLevel, message: string, containsPii: boolean) => {
          loggerCallback(logger, level, message, containsPii);
        },
        logLevel, // LogLevel.Verbose for debugging
        piiLoggingEnabled: false, // true for debugging
      },
    },
  });
}

/**
 * Handles msalinterceptorconfigfactory for this module.
 */
export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, string[]>(environment.protectedResourceMap as [string, string[]][]);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

/**
 * Handles msalguardconfigfactory for this module.
 */
export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    // authRequest: {
    //   scopes: ['user.read'],
    // },
    loginFailedRoute: '/auth/failed',
  };
}

@NgModule({ declarations: [
        AppComponent
    ],
    bootstrap: [AppComponent, MsalRedirectComponent],
    imports: [LoggerModule.forRoot({
            // serverLoggingUrl: '/api/logs', // TODO: Implement logging in server https://stackoverflow.com/a/53078568/2557554
            level: environment.loggingLevel,
            serverLogLevel: NgxLoggerLevel.ERROR,
        }),
        BrowserModule,
        BrowserAnimationsModule,
        IconsModule,
        AppRoutingModule,
        // ConnectionServiceModule,
        NgbModule,
        ThemeModule.forRoot(),
        CoreModule.forRoot(),
        MsalModule,
        GridModule,
        DropDownsModule,
        DialogsModule,
        LayoutModule,
        DateInputsModule,
        TooltipModule,
        SharedModule,
        InputsModule,
        NbThemeModule.forRoot({ name: 'corporate' }),
        NbLayoutModule,
        FramewerxKendoAngularGridCompatModule,
        ApiModule.forRoot(apiConfigFactory)], providers: [
        { provide: ErrorHandler, useClass: GlobalErrorHandlerService },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: GlobalHttpErrorInterceptorService,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: BlobErrorHttpInterceptorService,
            multi: true,
        },
        CldrIntlService,
        {
            provide: IntlService,
            useExisting: CldrIntlService,
        },
        {
          provide: ICON_SETTINGS,
          useValue: { type: 'svg' },
        },
        // LocalizationService,
        // FilterService,
        MeasurementTypeService,
        {
            provide: LOCALE_ID,
            useValue: 'en-CA',
        },
        { provide: APP_BASE_HREF, useValue: '/' },
        { provide: BASE_PATH, useValue: environment.apiBaseUrl },
        // {provide: STEMS_BASE_PATH, useValue: environment.stemsApiBaseUrl},
        {
            provide: HTTP_INTERCEPTORS,
            useClass: MsalCustomInterceptor,
            multi: true,
        },
        {
            provide: MSAL_INSTANCE,
            useFactory: MSALInstanceFactory,
            deps: [NGXLogger],
        },
        {
            provide: MSAL_GUARD_CONFIG,
            useFactory: MSALGuardConfigFactory,
        },
        {
            provide: MSAL_INTERCEPTOR_CONFIG,
            useFactory: MSALInterceptorConfigFactory,
        },
        {
            provide: POPUP_CONTAINER,
            useFactory: () => ({
                get nativeElement() {
                  return document.querySelector('nb-dialog-container') ?? document.body;
                }
              } as ElementRef)
        },
        MsalService,
        MsalGuard,
        MsalBroadcastService,
        ErrorService,
        PendingChangesGuard,
        { provide: KENDO_ANGULAR_GRID_CONFIG, useFactory: kendoGridConfigFactory },
        provideHttpClient(withInterceptorsFromDi()),
    ] })
/**
 * Handles appmodule for this module.
 */
export class AppModule {
}
